/**
 * vws-click-attribution.js — capture advertising click identifiers and the analytics
 * session/user identifiers into CART ATTRIBUTES at landing, so they arrive on the Shopify
 * ORDER and the ad-click-to-order join is a SQL join in our warehouse.
 *
 * Issue 4672 / plan docs/plans/signals-architecture-review.md R3.
 *
 * WHY THE ORDER AND NOT A VENDOR. Measured over 664 orders (2026-07-01 to 10-11):
 * landing_site_ref null on every order, note_attributes empty on every order, no click
 * identifier of any kind on the row, and landing_site_base_url carrying the path with the
 * query string stripped. The join therefore existed only inside a vendor we cannot query.
 * Elevar retired its own order-note write on purpose (vendor, 2026-10-13), so this path is
 * ours alone and nothing will collide with us on it.
 *
 * THE KEYS ARE UNDERSCORE-PREFIXED, which hides them from the admin order page while keeping
 * them in the Admin API payload and so in vws-raw. That is deliberate: cluttering the order
 * screen for the fulfilment team is one of the three reasons the vendor gave for abandoning
 * this approach. IT IS ALSO THE ONE UNVERIFIED LINK IN THE CHAIN — the proof that Fivetran
 * carries custom note attributes is a `shipping-token` key with no underscore. If the first
 * real order shows the keys present in Shopify and absent in vws-raw, drop the prefix; that is
 * a one-line change here. docs/gtm-first-party-cutover.md records the check.
 *
 * WHAT IT WRITES, AND WHEN. One POST to /cart/update.js, on a landing that carries at least
 * one click identifier in its query string. Organic landings write nothing, so the ordinary
 * visitor's first page view is untouched and no cart record is created for them. The attributes
 * persist on the cart server-side, so a visitor who adds to cart an hour later still carries
 * them; a cart write on an empty cart is the normal case here and is why the retry exists.
 *
 * LAST CLICK WINS. A later ad click in the same session overwrites the earlier one, matching
 * what every ad platform's own attribution does with the identifier we would send back.
 *
 * It does not depend on GTM, on the dataLayer, or on any app embed - which is the point. On
 * 2026-10-14 a full load of the home page from an ad-click URL produced no dl_user_data event
 * and no _ga cookie at all (lmlaptop, headless Chromium), so a capture that waited on the tag
 * layer would have captured nothing on that page view.
 */
(function () {
    'use strict';

    var CLICK_PARAMS = ['gclid', 'wbraid', 'gbraid', 'msclkid', 'fbclid'];
    var ATTR_PREFIX = '_vws_';
    var STORE_KEY = 'vws:click-attribution';
    var MAX_ATTEMPTS = 3;
    var MAX_VALUE = 256;
    var MAX_URL = 500;
    /* Click identifiers are opaque tokens; anything outside this set is not one of ours. */
    var SAFE_VALUE = /^[A-Za-z0-9._~-]+$/;

    function readCookie(name, cookieString) {
        var source = typeof cookieString === 'string' ? cookieString : document.cookie;
        var parts = source.split(';');
        for (var i = 0; i < parts.length; i++) {
            var pair = parts[i].trim();
            var eq = pair.indexOf('=');
            if (eq > 0 && pair.slice(0, eq) === name) return pair.slice(eq + 1);
        }
        return '';
    }

    function gaCookieName(cookieString) {
        var source = typeof cookieString === 'string' ? cookieString : document.cookie;
        var parts = source.split(';');
        for (var i = 0; i < parts.length; i++) {
            var name = parts[i].trim().split('=')[0];
            if (/^_ga_[A-Za-z0-9]+$/.test(name)) return name;
        }
        return '';
    }

    /* _ga is GA1.1.<client id>, e.g. GA1.1.1234567890.1699999999 -> 1234567890.1699999999 */
    function parseGaClientId(value) {
        if (!value) return '';
        var parts = value.split('.');
        if (parts.length >= 4) return parts.slice(2).join('.').slice(0, MAX_VALUE);
        return value.slice(0, MAX_VALUE);
    }

    /* GS2: GS2.1.s1739000000$o1$g0$... ; GS1: GS1.1.1699999999.1.0... . The format is
       Google's, not ours, so an unrecognised value is carried whole rather than dropped. */
    function parseGaSessionId(value) {
        if (!value) return '';
        var match = /(?:^|[.$])s(\d{8,})/.exec(value);
        if (match) return match[1];
        var parts = value.split('.');
        if (parts.length > 2 && /^\d{8,}$/.test(parts[2])) return parts[2];
        return value.slice(0, MAX_VALUE);
    }

    function cleanValue(raw) {
        if (!raw) return '';
        var value = String(raw).trim().slice(0, MAX_VALUE);
        return SAFE_VALUE.test(value) ? value : '';
    }

    /**
     * Pure: build the attribute map for one landing. Returns null when there is nothing to
     * write, so the caller never POSTs on an organic page view.
     */
    function collect(search, cookieString, href, nowIso) {
        var params = new URLSearchParams(search || '');
        var attributes = {};
        var found = 0;
        for (var i = 0; i < CLICK_PARAMS.length; i++) {
            var value = cleanValue(params.get(CLICK_PARAMS[i]));
            if (value) {
                attributes[ATTR_PREFIX + CLICK_PARAMS[i]] = value;
                found++;
            }
        }
        if (!found) return null;

        var clientId = parseGaClientId(readCookie('_ga', cookieString));
        if (clientId) attributes[ATTR_PREFIX + 'ga_client_id'] = clientId;
        var gaName = gaCookieName(cookieString);
        if (gaName) {
            var sessionId = parseGaSessionId(readCookie(gaName, cookieString));
            if (sessionId) {
                attributes[ATTR_PREFIX + 'ga_session_id'] = sessionId;
                attributes[ATTR_PREFIX + 'ga_stream'] = gaName.slice('_ga_'.length);
            }
        }
        /* The warehouse has the landing PATH already and never the query string, which is
           where the campaign parameters live. */
        if (href) attributes[ATTR_PREFIX + 'landing_url'] = String(href).slice(0, MAX_URL);
        attributes[ATTR_PREFIX + 'captured_at'] = nowIso;
        return attributes;
    }

    function signature(attributes) {
        var keys = Object.keys(attributes).filter(function (key) {
            return key !== ATTR_PREFIX + 'captured_at';
        }).sort();
        return keys.map(function (key) { return key + '=' + attributes[key]; }).join('&');
    }

    function readState() {
        try {
            return JSON.parse(window.sessionStorage.getItem(STORE_KEY) || 'null') || null;
        } catch (error) {
            return null;
        }
    }

    function writeState(state) {
        try {
            window.sessionStorage.setItem(STORE_KEY, JSON.stringify(state));
        } catch (error) {
            /* Private mode and blocked storage both land here. The capture still POSTs; it
               just cannot dedupe across page views, which costs a request, not data. */
        }
    }

    function cartUpdateUrl() {
        var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        return root.replace(/\/$/, '') + '/cart/update.js';
    }

    function post(attributes, state) {
        return fetch(cartUpdateUrl(), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ attributes: attributes })
        }).then(function (response) {
            if (!response.ok) throw new Error('cart/update.js ' + response.status);
            state.status = 'written';
            writeState(state);
        }).catch(function (error) {
            state.status = 'pending';
            writeState(state);
            if (window.console && window.console.warn) {
                window.console.warn('[vws-click-attribution] deferred:', error && error.message);
            }
        });
    }

    function run() {
        /* The theme editor loads pages repeatedly; a cart write there is noise in a real cart. */
        if (window.Shopify && window.Shopify.designMode) return;
        if (typeof fetch !== 'function' || typeof URLSearchParams !== 'function') return;

        var previous = readState();
        var attributes = collect(
            window.location.search,
            document.cookie,
            window.location.href,
            new Date().toISOString()
        );

        if (!attributes) {
            /* No click identifier on this page view. Finish an unwritten earlier capture. */
            if (previous && previous.status === 'pending' && previous.attempts < MAX_ATTEMPTS) {
                previous.attempts++;
                post(previous.attributes, previous);
            }
            return;
        }

        var current = signature(attributes);
        if (previous && previous.signature === current) {
            if (previous.status === 'written' || previous.attempts >= MAX_ATTEMPTS) return;
            previous.attempts++;
            post(previous.attributes, previous);
            return;
        }

        var state = { signature: current, attributes: attributes, status: 'pending', attempts: 1 };
        writeState(state);
        post(attributes, state);
    }

    /* Exposed for debugging and for a unit test that never needs a store: every function
       above is pure except run(), post() and the storage pair. */
    window.VwsClickAttribution = {
        collect: collect,
        readCookie: readCookie,
        gaCookieName: gaCookieName,
        parseGaClientId: parseGaClientId,
        parseGaSessionId: parseGaSessionId,
        signature: signature,
        run: run
    };

    run();
})();
